import {
  Component,
  computed,
  inject,
  OnChanges,
  signal,
  SimpleChanges,
  input,
  viewChild,
} from '@angular/core';
import {ControlValueAccessor, FormsModule, NgControl} from '@angular/forms';
import {Select} from 'primeng/select';
import {CountryCode} from '@frontend/directus';
import {CommonModule} from '@angular/common';
import {CountryService} from '@frontend/shared';

export interface CountryOption extends CountryCode {
  searchLabel: string;
}

@Component({
  selector: 'app-country-select',
  imports: [CommonModule, FormsModule, Select],
  templateUrl: './country-select.component.html',
  styleUrl: './country-select.component.scss',
})
export class CountrySelectComponent implements ControlValueAccessor, OnChanges {
  readonly placeholder = input<string>();
  readonly showValidation = input(true);
  readonly testId = input('country-select');
  readonly excludeCountryCodes = input<string[]>();

  readonly selectComponent = viewChild(Select);

  public readonly ngControl = inject(NgControl, {optional: true, self: true});

  readonly countryService = inject(CountryService);

  readonly touched = signal(false);
  readonly valueSignal = signal<string | null>(null);
  readonly excludeCountryCodesSignal = signal<string[]>([]);

  readonly countriesWithLabels = computed(() => {
    const countries = this.countryService.countriesSorted();
    const excluded = this.excludeCountryCodesSignal();
    const currentValue = this.valueSignal();

    return countries
      .filter((country) => !excluded.includes(country.isoCode) || country.isoCode === currentValue)
      .map((country) => ({
        ...country,
        searchLabel: this.getCountryDisplayName(country),
      })) as CountryOption[];
  });

  get value(): string | null {
    return this.valueSignal();
  }

  set value(val: string | null) {
    this.valueSignal.set(val);
  }

  disabled = false;

  private onChange!: (value: string | null) => void;
  private onTouched!: () => void;

  constructor() {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['excludeCountryCodes']) {
      this.excludeCountryCodesSignal.set(this.excludeCountryCodes() || []);
    }
  }

  onValueChange(value: string | null): void {
    this.value = value;
    this.touched.set(true);
    this.onChange(value);
    this.onTouched();

    const selectComponent = this.selectComponent();
    if (selectComponent) {
      selectComponent.resetFilter();
    }
  }

  onBlur(): void {
    this.touched.set(true);
    this.onTouched();
  }

  getCountryDisplayName(country: CountryCode): string {
    return this.countryService.getCountryDisplayName(country);
  }

  writeValue(value: string | null): void {
    this.value = value;
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
